// « Mes programmations » (#/programmations?semaine=YYYY-MM-DD): the coach's weekly programmations. Four blocks: week selector
// (published weeks, « Cette semaine »), « Séances disponibles » (grouped by programmation, AU CHOIX groups with the UBM
// SegmentedControl, cards on the UBM CardSurface, detail sheet before launch), « Consignes du coach » (real coach portrait, demo
// text) and « Ma semaine en bref » (UBM Progress, realised sessions only). Sessions launch the same Séance player. Small-group
// classes stay in Réserver. Demo fixtures (programmations-state.ts); nothing is sent.
import { Button } from "@ubm/ui/components/button";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDirectionalSwap } from "./motion-swap";
import { ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { CardSurface } from "@ubm/ui/components/card";
import { Progress } from "@ubm/ui/components/progress";
import { SegmentedControl } from "@ubm/ui/components/segmented-control";
import { Avatar } from "@ubm/ui/components/avatar";
import { StatusBar, type Route } from "./parts";
import { AccountSheet } from "./profil";
import { useStore } from "./store";
import { messagerie } from "./messagerie-state";
import { TEAM } from "./studio-fixtures";
import {
  CANONICAL_ID, CURRENT_WEEK, WEEKS, chosenIn, countedProgs, progState, realised, statusOf, useProgState, weekLabel, weekOfProg,
  type ProgSession, type Programmation,
} from "./programmations-state";
import { ShellActions } from "./shell";

interface PageProps { go: (route: Route) => void; onOutOfScope: (label: string) => void }
const readWeek = () => { const w = window.location.hash.match(/semaine=(\d{4}-\d{2}-\d{2})/)?.[1]; return w && WEEKS.includes(w) ? w : CURRENT_WEEK; };
const STATUS = { todo: "À faire", started: "En cours", done: "Terminée" } as const;
const coachOf = (p: Programmation) => TEAM[p.coach === "antoine" ? 1 : 0];
const mondayOf = (iso: string) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d.toISOString().slice(0, 10); };
const launch = (s: ProgSession) => { window.location.hash = s.id === CANONICAL_ID ? "#/seance" : `#/seance?id=${s.id}`; };
const result = (p: Programmation, s: ProgSession) => { const r = realised(p, s); window.location.hash = `#/suivi?semaine=${mondayOf(r?.date ?? p.week)}&prog=${p.id}`; };

export function Programmations({ go }: PageProps) {
  useStore(); useProgState();
  const [week, setWeek] = useState(readWeek);
  const dir = useRef(0);
  // Week content: persistent surface, directional swap retargeted from the live value (see motion-swap.ts).
  const bodyRef = useRef<HTMLDivElement>(null);
  useDirectionalSwap(bodyRef, week, dir.current, { offset: 16, duration: 200, easing: "cubic-bezier(.22, 1, .36, 1)", reducedDuration: 150 });
  useEffect(() => { const on = () => { const w = readWeek(); dir.current = w > week ? 1 : w < week ? -1 : 0; setWeek(w); }; window.addEventListener("hashchange", on); return () => window.removeEventListener("hashchange", on); }, [week]);
  const [detail, setDetail] = useState<{ p: Programmation; s: ProgSession } | null>(null);
  const at = WEEKS.indexOf(week);
  const move = (to: string) => { window.location.hash = `#/programmations?semaine=${to}`; };
  const progs = weekOfProg(week);
  const counted = countedProgs(week);
  const groups = [...new Set(progs.map(p => p.choice ?? p.id))];
  const total = counted.reduce((n, p) => n + p.sessions.length, 0);
  const done = counted.reduce((n, p) => n + p.sessions.filter(s => statusOf(p, s) === "done").length, 0);

  const action = (p: Programmation, s: ProgSession) => {
    const st = statusOf(p, s);
    if (st === "done") return { label: "Voir mon résultat", on: () => result(p, s) };
    if (!s.exercises?.length) return null; // nothing to play: no fake launch
    return { label: st === "started" ? "Reprendre" : "Commencer", on: () => launch(s) };
  };

  return <div className="cc-screen cc-account cc-prog">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-account-head">
        <button type="button" className="cc-icon-btn" aria-label="Retour à l'entraînement" onClick={() => { window.location.hash = "#/entrainement"; }}><ChevronLeft size={18} strokeWidth={2} /></button>
        <h1 className="cc-title">Mes programmations</h1>
        <ShellActions />
      </header>

      {/* 1 · Semaine */}
      <div className="cc-prog-week" role="group" aria-label="Semaine">
        <button type="button" className="cc-icon-btn" aria-label="Semaine précédente" disabled={at <= 0} onClick={() => move(WEEKS[at - 1]!)}><ChevronLeft size={18} strokeWidth={2} /></button>
        <p className="cc-prog-week-label" aria-live="polite">{weekLabel(week)}</p>
        <button type="button" className="cc-icon-btn" aria-label="Semaine suivante" disabled={at >= WEEKS.length - 1} onClick={() => move(WEEKS[at + 1]!)}><ChevronRight size={18} strokeWidth={2} /></button>
      </div>
      {week !== CURRENT_WEEK && <Button variant="ghost" size="sm" className="cc-prog-now" onClick={() => move(CURRENT_WEEK)}>Cette semaine</Button>}

      <div ref={bodyRef} className="cc-prog-body"><Fragment key={week}>
        {progs.length === 0 ? <div className="cc-card cc-prog-empty">
          <p>Ton coach n’a pas encore partagé de programmation pour cette semaine.</p>
          <Button variant="ghost" size="sm" onClick={() => { window.location.hash = "#/reserver"; }}>Les cours en petit groupe se réservent dans Réserver <ChevronRight size={13} strokeWidth={2.4} /></Button>
        </div> : <>
          {/* 2 · Séances disponibles */}
          <section className="cc-prog-block" aria-labelledby="pg-seances">
            <h2 id="pg-seances" className="cc-account-group-title">Séances disponibles</h2>
            {groups.map(g => {
              const options = progs.filter(p => (p.choice ?? p.id) === g);
              const p = options.length > 1 ? options.find(o => o.id === chosenIn(g)) ?? options[0]! : options[0]!;
              return <div key={g} className="cc-prog-group">
                <div className="cc-prog-group-head"><b>{options.length > 1 ? "Au choix" : p.name}</b><small>{options.length > 1 ? "Une seule des deux compte cette semaine" : `${p.theme} · avec ${coachOf(p)?.firstName ?? "ton coach"}`}</small></div>
                {options.length > 1 && <SegmentedControl size="sm" label="Programmation au choix" value={p.id} onValueChange={v => progState.choose(g, v)} options={options.map(o => ({ value: o.id, label: o.name }))} />}
                <ol className="cc-prog-sessions">{p.sessions.map((s, i) => {
                  const st = statusOf(p, s), act = action(p, s);
                  return <li key={s.id}><CardSurface variant="raised" padding="sm" radius="lg" className="cc-prog-card" data-status={st}>
                    <button type="button" className="cc-prog-card-open" aria-label={`Détail · ${s.title}`} onClick={() => setDetail({ p, s })}>
                      <span className="cc-prog-index" aria-hidden="true">{i + 1}</span>
                      <span className="cc-prog-card-text"><b>{s.title}</b><small>{[options.length > 1 ? p.name : p.theme, `${s.minutes} min`, s.exercises?.length ? `${s.exercises.length} exercices` : null].filter(Boolean).join(" · ")}</small></span>
                      <span className="cc-prog-pill" data-status={st}>{STATUS[st]}</span>
                    </button>
                    {act && <button type="button" className="cc-prog-act" onClick={act.on}>{act.label}</button>}
                  </CardSurface></li>;
                })}</ol>
              </div>;
            })}
          </section>

          {/* 3 · Consignes du coach */}
          {counted.some(p => p.notes?.length) && <section className="cc-prog-block" aria-labelledby="pg-consignes">
            <h2 id="pg-consignes" className="cc-account-group-title">Consignes du coach</h2>
            {counted.filter(p => p.notes?.length).map(p => { const c = coachOf(p); return <div key={p.id} className="cc-card cc-prog-notes">
              <div className="cc-prog-notes-head">{c && <Avatar name={c.firstName} src={c.photo} size="md" />}<span><b>{c?.firstName ?? "Ton coach"}</b><small>{p.name} · texte de démonstration</small></span></div>
              <ul>{p.notes!.map(n => <li key={n}>{n}</li>)}</ul>
              <Button variant="ghost" size="sm" onClick={() => { messagerie.startWithContext(p.coach, { label: `À propos de ${p.name} · semaine du ${weekLabel(week)}`, href: `#/programmations?semaine=${week}&prog=${p.id}` }); window.location.hash = `#/messages?fil=${p.coach}`; }}><MessageCircle size={13} strokeWidth={2.2} /> Poser une question</Button>
            </div>; })}
          </section>}

          {/* 4 · Ma semaine en bref */}
          {total > 0 && <section className="cc-prog-block" aria-labelledby="pg-bref">
            <h2 id="pg-bref" className="cc-account-group-title">Ma semaine en bref</h2>
            <div className="cc-card cc-prog-bref">
              <p><b>{done}</b> séance{done > 1 ? "s" : ""} terminée{done > 1 ? "s" : ""} sur {total}</p>
              <Progress value={done} max={total} label="Séances terminées cette semaine" />
              <small className="cc-bp-note">Seules les séances réalisées comptent ; les cours réservés et les séances en cours ne comptent pas.</small>
              <Button variant="ghost" size="sm" onClick={() => { window.location.hash = `#/suivi?semaine=${week}&prog=${counted.map(p => p.id).join(",")}`; }}>Voir mes séances réalisées <ChevronRight size={13} strokeWidth={2.4} /></Button>
            </div>
          </section>}
        </>}
      </Fragment></div>
      <p className="cc-home-demo cc-account-demo">Prototype : programmations de démonstration, contenus et consignes fictifs.</p>
    </div>

    {detail && <AccountSheet title={detail.s.title} onClose={() => setDetail(null)}>
      <p className="cc-bp-note">{detail.p.name} · {detail.s.minutes} min · {STATUS[statusOf(detail.p, detail.s)]}</p>
      {detail.s.exercises?.length ? <ol className="cc-home-ex">{detail.s.exercises.map(x => <li key={x}>{x}</li>)}</ol>
        : <p className="cc-bp-note">Le détail des exercices n'est pas encore partagé par ton coach.</p>}
      {(() => { const act = action(detail.p, detail.s); return act ? <Button variant="primary" size="lg" className="cc-cta" onClick={() => { setDetail(null); act.on(); }}>{act.label}</Button> : null; })()}
    </AccountSheet>}
  </div>;
}
